import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Plus, X } from 'lucide-react';

import { history } from '../../store/store.ts';
import { greyP5 } from '../../theme.ts';
import { Project } from '../../types/project.ts';

interface SidebarProps {
  selectedProject?: Project | null;
  onOpenCreateFolderModal: () => void;
  onOpenDeleteModal: () => void;
  onOpenMoveModal: () => void;
  onOpenRenameModal: () => void;
  onCreateProject?: () => void;
}

const Sidebar: React.FC<SidebarProps> = ({
  selectedProject,
  onOpenCreateFolderModal,
  onOpenDeleteModal,
  onOpenMoveModal,
  onOpenRenameModal,
  onCreateProject,
}) => {
  const { t } = useTranslation();
  const [selectedCategory, setSelectedCategory] = useState<string>('yourProjects');

  const handleCreateNewProject = () => {
    if (onCreateProject) {
      onCreateProject();
    } else {
      history.push('/new-project');
    }
  };

  const fileCategories = [
    { name: 'yourProjects', label: t('projects.sidebar.yourProjects') },
    {
      name: 'sharedWithYou',
      label: t('projects.sidebar.sharedWithYou'),
      comingSoon: (
        <span className="bg-greyP5 text-black text-[10px] px-2 font-extrabold p-1 rounded-full">
          {t('common.comingSoon')}
        </span>
      ),
    },
    {
      name: 'deletedProjects',
      label: t('projects.sidebar.deletedProjects'),
      comingSoon: (
        <span className="bg-greyP5 text-black text-[10px] px-2 p-1 font-extrabold rounded-full">
          {t('common.comingSoon')}
        </span>
      ),
    },
  ];

  const creationActions = [
    { label: t('projects.sidebar.newProject'), icon: Plus, primary: true, onClick: () => handleCreateNewProject() },
    { label: t('projects.sidebar.newFolder'), icon: Plus, primary: false, onClick: () => onOpenCreateFolderModal() },
  ];

  const editingActions = [
    {
      label: t('common.open'),
      icon: X,
      onClick: () => (window.location.href = `/workspace?projectId=${selectedProject?.id}`),
    },
    { label: t('common.rename'), icon: X, onClick: () => onOpenRenameModal() },
    { label: t('common.move'), icon: X, onClick: () => onOpenMoveModal() },
    { label: t('common.delete'), icon: X, onClick: () => onOpenDeleteModal() },
  ];

  return (
    <>
      <nav
        className="flex-shrink-0 hidden md:inline-flex flex-col border-r"
        style={{
          width: '240px',
          height: 'calc(100vh - 224px)',
          borderColor: greyP5,
        }}
        data-testid="sidebar-nav"
      >
        <div className="bg-black overflow-y-auto h-full">
          <div className="flex flex-col space-y-6">
            <div className="space-y-0">
              {fileCategories.map((category) => (
                <button
                  key={category.name}
                  onClick={() => setSelectedCategory(category.name)}
                  disabled={!!category.comingSoon}
                  className={`flex flex-row items-center justify-between w-full h-[44px] gap-2 text-left px-8 text-white font-medium
                    disabled:cursor-not-allowed  
                    border-b border-greyP5 transition-colors duration-200 text-xs ${
                      selectedCategory === category.name ? 'bg-greyP5' : 'hover:bg-greyP5'
                    }`}
                >
                  <div>{category.label}</div>
                  {category.comingSoon}
                </button>
              ))}
            </div>
          </div>

          <div className="flex flex-col p-4 space-y-6">
            <div className="space-y-3">
              {creationActions.map((action) => {
                const IconComponent = action.icon;
                return (
                  <button
                    key={action.label}
                    onClick={action.onClick}
                    className={`flex items-center justify-between px-6 py-3 rounded-full font-medium transition-colors duration-200 text-xs ${
                      action.primary
                        ? 'bg-white text-black hover:bg-gray-100'
                        : 'border border-gray-600 text-white hover:bg-greyP5 hover:border-gray-500'
                    }`}
                    style={{
                      height: '36px',
                      paddingTop: '10px',
                      paddingBottom: '10px',
                      paddingLeft: '18px',
                      paddingRight: '18px',
                      gap: '8px',
                    }}
                  >
                    <IconComponent className="w-5 h-5" />
                    {action.label}
                  </button>
                );
              })}
            </div>

            {selectedProject && (
              <div className="space-y-3">
                <h3 className="text-green01 font-medium text-sm px-2">
                  {t('sidebar.editProject')} -{' '}
                  <strong className="text-sm">
                    {selectedProject.name.length > 20
                      ? selectedProject.name.substring(0, 20) + '...'
                      : selectedProject.name}
                  </strong>
                </h3>
                {editingActions.map((action) => {
                  const IconComponent = action.icon;
                  return (
                    <button
                      key={action.label}
                      onClick={action.onClick}
                      className="flex items-center px-6 py-3 rounded-full border border-green01 text-green01 font-medium hover:bg-greyP5 hover:border-greyP6 hover:text-white transition-colors duration-200 text-xs"
                      style={{
                        height: '36px',
                        paddingTop: '10px',
                        paddingBottom: '10px',
                        paddingLeft: '18px',
                        paddingRight: '18px',
                      }}
                    >
                      <IconComponent className="w-4 h-4 mr-2" />
                      {action.label}
                    </button>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      </nav>
    </>
  );
};

export default Sidebar;
